/**
 * IMPACTS (v8.326.0) - what just happened to somebody, for the map and the
 * card to show for a moment.
 *
 * The log already records every attack as a structured entry with an
 * outcome, a total and a status; this reads the newest of those and turns
 * them into short-lived facts a marker can wear: the number that came off,
 * MISS, DEFLECTED, DEAD. Nothing is stored and nothing is rolled - an
 * impact is a projection of an entry that exists, kept only long enough
 * to be seen (the shell prunes them after a couple of seconds).
 *
 * Two sources: the local log of each tab (my own attacks, and everything
 * in solo) and the shared log (other players' attacks, matched to a
 * combatant by target name, since the shared document carries no id).
 * Both are watched by a high-water mark, so history never replays.
 *
 * Pure, zero React dependency.
 */
import type { Tab } from '../types';
import type { Combatant } from './roster/types';
import {
  type AreaBurst,
  readAttackBeat,
  struckId,
  type AttackBeat,
  type AttackKind,
  type RateOfFire,
} from './attackBeat';
import { collectFromLogs, type LogLine, type LogMarks } from './log/collect';
import { actionById } from './actions/catalog';
import { getBodyConfig } from './bodyConfig';
import { BODY_SHAPES } from './bodyShape';
import { isDodgeType } from './defenceChain';
import type { Signature } from './stealth/noise';

/**
 * `emerge`: a hidden figure revealed (plan map-display, G2) - not an attack, its arrival out of hiding. `action`: a
 * body action (plan design-gaps, K2) - Knock-Down, Stun, Feint or Grapple - no wound (its `kind` says how it plays).
 */
type ImpactOutcome = 'hit' | 'partial' | 'deflected' | 'blocked' | 'miss' | 'jam' | 'emerge' | 'action';

export interface Impact {
  /** `${combatantId}:${at}` - the same attack seen twice is one impact. */
  id: string;
  combatantId: string;
  outcome: ImpactOutcome;
  /** Wounds or magnitude taken; 0 for a miss. */
  damage: number;
  dead: boolean;
  /** Undying refused the death this attack dealt (present only then): it falls, lies dead and stands again. */
  cheatedDeath?: boolean;
  /** The entry's one-line summary, for the card's strip. */
  summary: string;
  at: number;
  /**
   * What the display's beat reads (plan map-display, D). The attacker is the
   * acting combatant's id as the entry carries it - veiled at the sync
   * boundary, so a hidden attacker reaches a player as its alias and matches
   * no marker there. Null on an entry older than the field.
   */
  attackerId: string | null;
  melee: boolean;
  /** The weapon's noise, for the display's ring in Stealth (plan map-display, H); null when unsaid. */
  noise: 'loud' | 'silent' | null;
  /** The weapon's Signature step (plan armoury): how far that ring spreads. Null when unsaid. */
  signature?: Signature | null;
  /** I, E, R or X; null when the entry does not say. */
  damageType: DamageCode | null;
  /** A critical effect, or a confirmed Righteous Fury. */
  crit: boolean;
  /** A parry held: the defender's own circle on the display. */
  parried: boolean;
  /** Each hit that reached the target after the defence, in order (plan body-map, R4): what a plate plays. */
  hits: ImpactHit[];
  /** Hits a dodge took off the front of the burst: they glance off first. */
  dodged: number;
  /** The weapon's special rules, off the beat: the accents a plate and the map play. */
  specials: string[];
  /** Looks a weapon rule lent (plan armoury): played like specials, never read as one. */
  looks?: string[];
  /** The action it was made under, off the beat (plan design-gaps, K1); absent reads as standard. */
  kind?: AttackKind;
  /** The rate of fire, off the beat; absent or a melee attack reads as none. */
  rof?: RateOfFire | null;
  /** The cover's AP the hits met, off the beat; absent reads as 0. */
  coverAp?: number;
  /** A body action's impact (`outcome: 'action'`): its catalog id, and whether it went the actor's way. */
  action?: string;
  won?: boolean;
  /** The blast template this victim was caught in (plan design-gaps, T2); absent for anything else. */
  area?: AreaBurst;
}

/** One landed hit, as a plate plays it. */
export interface ImpactHit {
  /** The location's engine id (head, rightArm, hull, mass...), or null when the entry names none it knows. */
  location: string | null;
  damageType: DamageCode | null;
  /** Wounds it took. */
  effective: number;
  crit: boolean;
  /** A force field stopped it; `overloaded` when that block burned the field out. */
  blocked: boolean;
  overloaded: boolean;
  /** Daemonic protection soaked some of it. */
  soaked: boolean;
}

/** A location's NAME to its engine id, over every body: for rows written before the row carried its id. */
const LOCATION_IDS = new Map(BODY_SHAPES.flatMap((b) => getBodyConfig(b).locations.map((l) => [l.name, l.id])));

/** The four damage types the beats draw. */
export type DamageCode = 'I' | 'E' | 'R' | 'X';
const DAMAGE_CODES = new Map<string, DamageCode>([
  ['I', 'I'],
  ['Impact', 'I'],
  ['E', 'E'],
  ['Energy', 'E'],
  ['R', 'R'],
  ['Rending', 'R'],
  ['X', 'X'],
  ['Explosive', 'X'],
]);

interface EntryLike {
  type?: string;
  /** A string on an old entry; an object (`success`, `jam`) on today's - which the shared log does not keep. */
  result?: unknown;
  text?: string;
  totalDamage?: number;
  magDmg?: number;
  timestamp?: number;
  targetName?: string;
  structured?: unknown;
  /** Who struck, melee, landed/miss/jam (engine/attackBeat) - on every attack entry, in the shared log too. */
  beat?: unknown;
}

interface StructuredLike {
  outcome?: string;
  summary?: string;
  detail?: {
    status?: { isDead?: boolean; isDestroyed?: boolean; cheatedDeath?: boolean; totalDamage?: number; magDmg?: number };
    damageBreakdown?: BreakdownLike[];
    criticalEffects?: unknown[];
    defenceDetails?: { type?: string; success?: boolean; avoided?: number }[];
  };
}

interface BreakdownLike {
  location?: string;
  locationId?: string;
  damageType?: string;
  rfConfirmed?: boolean;
  effective?: number;
  crit?: unknown;
  ffBlocked?: boolean;
  ffOverloaded?: boolean;
  daemonicProt?: number;
}

/** A breakdown row as a plate plays it. */
function impactHit(h: BreakdownLike): ImpactHit {
  return {
    location: h.locationId ?? LOCATION_IDS.get(h.location ?? '') ?? null,
    damageType: DAMAGE_CODES.get(h.damageType ?? '') ?? null,
    effective: h.effective ?? 0,
    crit: !!h.crit || !!h.rfConfirmed,
    blocked: !!h.ffBlocked,
    overloaded: !!h.ffOverloaded,
    soaked: (h.daemonicProt ?? 0) > 0,
  };
}

/** The landed hits and the dodged count: what a plate plays (plan body-map, R4). */
function burstFacts(s: StructuredLike | null) {
  const detail = s?.detail;
  const dodges = (detail?.defenceDetails ?? []).filter((d) => d.success && isDodgeType(d.type));
  return {
    hits: (detail?.damageBreakdown ?? []).map(impactHit),
    dodged: dodges.reduce((n, d) => n + (d.avoided ?? 0), 0),
  };
}

/** An entry's structured twin, stored as an object or as the string a backend kept; null when it has none. */
export function structuredOf(entry: EntryLike): StructuredLike | null {
  const s = entry.structured;
  if (!s) return null;
  if (typeof s === 'string') {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }
  return s as StructuredLike;
}

/** What the beat reads off the entry: who struck (its beat column), with what, and how it was met (structured). */
function beatFacts(s: StructuredLike | null, beat: AttackBeat | null) {
  const detail = s?.detail;
  const hits = detail?.damageBreakdown ?? [];
  return {
    attackerId: beat?.actorId ?? null,
    melee: !!beat?.melee,
    noise: beat?.noise ?? null,
    signature: beat?.signature ?? null,
    specials: beat?.specials ?? [],
    ...(beat?.looks.length ? { looks: beat.looks } : {}),
    kind: beat?.kind ?? 'standard',
    rof: beat?.rof ?? null,
    coverAp: beat?.coverAp ?? 0,
    // A blast's template (T2), only on a victim of one: every other impact stays as it was.
    ...(beat?.area ? { area: beat.area } : {}),
    damageType: DAMAGE_CODES.get(hits.find((h) => h.damageType)?.damageType ?? '') ?? null,
    crit: !!detail?.criticalEffects?.length || hits.some((h) => h.rfConfirmed),
    parried: !!detail?.defenceDetails?.some((d) => d.success && d.type === 'parry'),
  };
}

/** What an entry's own `result` says: a string on an old entry, an object (`jam`, `success`) on today's. */
function resultOutcome(r: unknown): ImpactOutcome | null {
  if (r === 'miss' || r === 'jam') return r;
  if (!r || typeof r !== 'object') return null;
  if ((r as { jam?: boolean }).jam) return 'jam';
  return (r as { success?: boolean }).success === false ? 'miss' : null;
}

/**
 * An entry without a structured outcome (a miss, a jam, or an old entry): its beat column (the shared log
 * keeps that, and not `result`), else its result, else whether damage landed.
 */
function plainOutcome(entry: EntryLike, beat: AttackBeat | null, damage: number): ImpactOutcome {
  const said = beat?.outcome;
  if (said === 'miss' || said === 'jam') return said;
  return resultOutcome(entry.result) ?? (damage > 0 ? 'hit' : 'blocked');
}

/** Wounds or magnitude taken: the entry's own fields, else (the shared log keeps neither) its structured status. */
function damageOf(entry: EntryLike, s: StructuredLike | null): number {
  const status = s?.detail?.status;
  return entry.totalDamage ?? entry.magDmg ?? status?.totalDamage ?? status?.magDmg ?? 0;
}

/**
 * Whether a log line owes a beat on a display: an attack that went off (a jam fires nothing), or any line carrying an
 * action beat - a body action's test line (K2), a horde's split or break on its story line (V4). Beside `impactOf`, so
 * the fuzzer's oracle (fuzz/snapshot) and what the display plays change in one file.
 */
export const owesBeat = (type: string | undefined, beat: AttackBeat | null): boolean =>
  !!beat?.action || (type === 'attack-result' && beat?.outcome !== 'jam');

/** The impact an attack entry describes, or null for any other entry. */
export function impactOf(
  entry: EntryLike,
  combatantId: string,
  beat: AttackBeat | null = readAttackBeat(entry.beat),
): Impact | null {
  if (typeof entry.timestamp !== 'number') return null;
  // The line's own time (the shared copy is stamped again): one attack, heard twice, is one impact.
  const at = beat?.at ?? entry.timestamp;
  if (beat?.emerge) return emergeOf(entry, combatantId, at);
  // A body action on its test line (K2), or a horde's split or break on its story line (V4).
  if (beat?.action) return actionOf(entry, combatantId, at, beat);
  if (entry.type !== 'attack-result') return null;
  const s = structuredOf(entry);
  const damage = damageOf(entry, s);
  const outcome: ImpactOutcome = (s?.outcome as ImpactOutcome | undefined) ?? plainOutcome(entry, beat, damage);
  return {
    id: `${combatantId}:${at}`,
    combatantId,
    outcome,
    damage,
    dead: !!(s?.detail?.status?.isDead || s?.detail?.status?.isDestroyed),
    ...(s?.detail?.status?.cheatedDeath ? { cheatedDeath: true } : {}),
    summary: s?.summary ?? entry.text ?? '',
    at,
    ...beatFacts(s, beat),
    ...burstFacts(s),
  };
}

/** A body action's line as an impact (plan design-gaps, K2): the actor's rush, no wound, won or lost. */
function actionOf(entry: EntryLike, combatantId: string, at: number, beat: AttackBeat): Impact {
  return {
    ...emergeOf(entry, combatantId, at),
    outcome: 'action',
    attackerId: beat.actorId,
    melee: true,
    kind: beat.kind,
    action: beat.action ?? undefined,
    won: beat.outcome === 'landed',
  };
}

/** A reveal's line as an impact: the figure arriving out of hiding, struck by nothing. */
function emergeOf(entry: EntryLike, combatantId: string, at: number): Impact {
  return {
    id: `${combatantId}:${at}`,
    combatantId,
    outcome: 'emerge',
    damage: 0,
    dead: false,
    summary: entry.text ?? '',
    at,
    attackerId: null,
    melee: false,
    noise: null,
    signature: null,
    damageType: null,
    crit: false,
    parried: false,
    hits: [],
    dodged: 0,
    specials: [],
  };
}

/** The marks the impacts are read past (engine/log/collect). */
export type ImpactWatermarks = LogMarks;

/**
 * Every impact newer than the marks, and the marks moved up (engine/log/collect: a tab seen for the first time
 * starts at `now`, so a roster that arrives with a history does not replay it as a fusillade). A tab's own lines
 * are about its target; a shared line's target is read off its column, or its name.
 */
export function collectImpacts(
  tabs: readonly Tab[],
  shared: readonly EntryLike[],
  roster: readonly Combatant[],
  marks: ImpactWatermarks,
  now: number,
): { impacts: Impact[]; marks: ImpactWatermarks } {
  const { found, marks: next } = collectFromLogs(tabs, shared as LogLine[], marks, now, {
    local: (entry, tabId) => impactOf(entry as EntryLike, tabId),
    shared: (entry) => sharedImpact(entry as EntryLike, roster),
  });
  return { impacts: found, marks: next };
}

/** A shared line's impact on the combatant it struck (attackBeat `struckId`), its column parsed once; or null. */
function sharedImpact(entry: EntryLike, roster: readonly Combatant[]): Impact | null {
  const beat = readAttackBeat(entry.beat);
  const id = struckId(beat, entry.targetName, roster);
  return id ? impactOf(entry, id, beat) : null;
}

/** How long a marker wears an impact, and the card its strip. */
export const IMPACT_TTL_MS = 2500;

/** The word a marker shows for an outcome without a number. */
export function impactLabel(impact: Impact): string {
  if (impact.dead) return 'DEAD';
  if (impact.damage > 0) return `-${impact.damage}`;
  switch (impact.outcome) {
    case 'miss':
      return 'MISS';
    case 'jam':
      return 'JAM';
    case 'deflected':
      return 'DEFLECTED';
    case 'blocked':
      return 'BLOCKED';
    case 'emerge':
      return 'EMERGES';
    case 'action':
      return actionLabel(impact);
    default:
      return 'HIT';
  }
}

/** What a horde's own beat says on its marker (V4). */
const HORDE_WORDS: Partial<Record<string, string>> = { split: 'SPLITS', break: 'BREAKS' };

/**
 * What a body action did to the target (K2; the Track K review): its catalog row's verb when it won, else HELD; a
 * horde's split or break says what it did (V4).
 */
function actionLabel(impact: Impact): string {
  const horde = HORDE_WORDS[impact.kind ?? ''];
  if (horde) return horde;
  return impact.won ? (actionById(impact.action ?? '')?.beat?.verb ?? 'HIT') : 'HELD';
}

/**
 * Whether `next` takes the marker from `held`: an impact that plays beats a jam, and of two alike the newer. A jam
 * plays no beat (display/beats), so a hit followed by a jam in one delivery played nothing on every screen but the
 * shooter's (the fuzzer's PocketBase missing beat, 2026-11-01).
 */
const takesOver = (held: Impact | undefined, next: Impact) => {
  if (!held) return true;
  const [heldJam, nextJam] = [held.outcome === 'jam', next.outcome === 'jam'];
  return heldJam === nextJam ? held.at < next.at : heldJam;
};

/** The newest impact per combatant, older than `ttl` dropped; a jam never replaces one that plays. */
export function liveImpacts(impacts: readonly Impact[], now: number, ttl = IMPACT_TTL_MS): Record<string, Impact> {
  const out: Record<string, Impact> = {};
  for (const i of impacts) {
    if (now - i.at > ttl) continue;
    if (takesOver(out[i.combatantId], i)) out[i.combatantId] = i;
  }
  return out;
}
